'use client';

import Image from '@/lib/app-image';
import { config, getTemplateAssetPath } from '@/config';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  FaPlaneArrival,
  FaPlaneDeparture,
  FaCalendarAlt,
  FaUsers,
} from 'react-icons/fa';
import { Loader as LoaderIcon, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';

interface LoaderProps {
  size?: number;
  src?: string;
  className?: string;
}

const loaderGif = getTemplateAssetPath('loader');

export function Loader({ size = 40, src, className }: LoaderProps) {
  const loaderSrc = src || loaderGif;

  return (
    <div className={`flex items-center justify-center ${className || ''}`}>
      <Image
        src={loaderSrc}
        alt="Loading..."
        width={size}
        height={size}
        priority
      />
    </div>
  );
}

interface FlightLoaderProps {
  message?: string;
  className?: string;
  totalFlights?: number;
  loadedFlights?: number;
}

export function FlightLoader({
  message = 'Please wait while we fetch the best flights for you...',
  className,
  totalFlights = 0,
  loadedFlights = 0,
}: FlightLoaderProps) {
  const flightSearch = useSelector((state: any) => state.flightSearch);
  const {
    origin_city,
    originCountry,
    origin,
    destination_city,
    destinationCountry,
    destination,
    date,
    rt_date,
    passengers,
    travelType,
  } = flightSearch || {};
  // State for animated progress
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Calculate progress percentage
  const targetProgress =
    totalFlights > 0 ? (loadedFlights / totalFlights) * 100 : 0;

  // Smoothly animate progress to target
  useEffect(() => {
    const animationDuration = 500; // Duration in milliseconds
    const steps = 60; // Number of steps in animation
    const stepDuration = animationDuration / steps;
    const increment = (targetProgress - animatedProgress) / steps;

    if (targetProgress !== animatedProgress) {
      const timer = setInterval(() => {
        setAnimatedProgress((prev) => {
          const next = prev + increment;
          if (
            (increment > 0 && next >= targetProgress) ||
            (increment < 0 && next <= targetProgress)
          ) {
            clearInterval(timer);
            return targetProgress;
          }
          return next;
        });
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [targetProgress, animatedProgress]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4">
      <div className="mx-auto w-full max-w-md rounded-xl bg-white shadow-lg">
        <div className="flex flex-col items-center space-y-4 p-4 sm:space-y-6 sm:p-6">
          <Image
            src={getTemplateAssetPath('logo')}
            alt={`${config.corporateName}`}
            width={180}
            height={60}
            className={`${config.corporateName === 'DMCLeisure' ? 'w-16 sm:w-8 md:w-20' : 'w-34'}`}
          />

          <style>{`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }

            @keyframes bounce {
              0%,
              100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-3px);
              }
            }

            .dot {
              display: inline-block;
              animation: bounce 1s infinite;
            }

            .dot:nth-child(1) {
              animation-delay: 0s;
            }
            .dot:nth-child(2) {
              animation-delay: 0.2s;
            }
            .dot:nth-child(3) {
              animation-delay: 0.4s;
            }

            .spin {
              animation: spin 2s linear infinite;
            }
          `}</style>

          <h2 className="mb-2 flex items-center gap-2 text-xl font-semibold sm:mb-4 sm:text-2xl">
            <Settings className="spin h-4 w-4 sm:h-5 sm:w-5" />
            Please Wait<span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
          </h2>
          <p className="mb-4 text-sm text-gray-600 sm:mb-8 sm:text-base">
            We are looking for all available flights for
          </p>

          <div className="w-full max-w-2xl px-2 sm:px-4">
            <div className="relative mb-4 flex items-center justify-between sm:mb-8">
              <div className="w-8 flex-1 md:max-w-28">
                <div className="text-base font-medium sm:text-lg">
                  {origin_city}
                </div>
                <div className="text-xs text-gray-500 sm:text-sm">{`${origin} ${originCountry}`}</div>
              </div>

              <div className="absolute left-1/2 top-1/2 flex h-[2px] w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center sm:w-32">
                <div className="w-full border-t-2 border-dotted border-gray-300"></div>
                <div className="absolute left-0 h-1.5 w-1.5 rounded-full bg-gray-300 sm:h-2 sm:w-2"></div>
                <div className="absolute right-0 h-1.5 w-1.5 rounded-full bg-gray-300 sm:h-2 sm:w-2"></div>
              </div>

              <div className="w-8 flex-1 text-right md:max-w-28">
                <div className="text-base font-medium sm:text-lg">
                  {destination_city}
                </div>
                <div className="text-xs text-gray-500 sm:text-sm">{`${destination}, ${destinationCountry}`}</div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {travelType === 'OW' ? (
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <FaCalendarAlt className="h-4 w-4 text-gray-500 sm:h-5 sm:w-5" />
                  <div>
                    <div className="text-sm font-medium sm:text-base">
                      {date ? format(new Date(date), 'dd-MMM-yyyy') : ''}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-row justify-between">
                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                    <FaPlaneDeparture className="h-5 w-5 text-gray-500 sm:h-6 sm:w-6" />
                    <div>
                      <div className="text-xs text-gray-500 sm:text-sm">
                        Departure
                      </div>
                      <div className="text-sm font-medium sm:text-base">
                        {date ? format(new Date(date), 'dd-MMM-yyyy') : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                    <FaPlaneArrival className="h-5 w-5 text-gray-500 sm:h-6 sm:w-6" />
                    <div>
                      <div className="text-xs text-gray-500 sm:text-sm">
                        Return
                      </div>
                      <div className="text-sm font-medium sm:text-base">
                        {rt_date
                          ? format(new Date(rt_date), 'dd-MMM-yyyy')
                          : ''}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <FaUsers className="h-4 w-4 text-gray-500 sm:h-5 sm:w-5" />
                <div>
                  <div className="text-sm font-medium sm:text-base">
                    {(() => {
                      const totalPassengers =
                        passengers?.adult +
                        passengers?.child +
                        passengers?.infant;
                      return `${totalPassengers} ${totalPassengers === 1 ? 'Passenger' : 'Passengers'}`;
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-8">
              {/* <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Loading flights...</span>
                <span className="text-sm text-gray-600">{loadedFlights} of {totalFlights} flights</span>
              </div> */}
              {/* <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-500 h-full rounded-full relative transition-all duration-500 ease-in-out"
                  style={{ width: `${animatedProgress}%` }}
                >
                  <div
                    className="absolute -right-3 -top-2.5 flex items-center justify-center animate-pulse"
                    style={{
                      transform: `translateX(${animatedProgress}%)`,
                      transition: 'transform 500ms ease-in-out'
                    }}
                  >
                    <div className="bg-blue-500 rounded-full p-2">
                      <FaPlaneDeparture
                        className="text-white text-sm transform -rotate-45"
                      />
                    </div>
                  </div>
                </div>
              </div> */}
              <div className="mt-4 text-center">
                <div className="animate-pulse text-xs text-gray-500 sm:text-sm">
                  {loadedFlights === 0
                    ? 'Searching for the best deals...'
                    : loadedFlights === totalFlights
                      ? 'Almost ready...'
                      : 'Finding the best flights for you...'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Example usage
export function LoaderDemo() {
  return (
    <div className="flex items-center space-x-4">
      <Loader />
      <Loader size={60} src="/Travelone/logo.svg" />
    </div>
  );
}

// import { Spinner, RoundSpinner, Dots_v1, Dots_v2, Dots_v3, Dots_v4 } from "@/components/ui/spinner"

// export function SpinnerDemo() {
//     return (
//         <div className="flex items-center space-x-2">
//             <Spinner size="5" color="border-light" />
//         </div>
//     )
// }
